import { camelizeResponse } from './keyMap'
import type { EventPageData, SignupState } from './types'

const ENDPOINT = '/webshared/controllers/eventPage.cfc'

export class EventPageApiError extends Error {}

export async function fetchEventPageData(eventId: number): Promise<EventPageData> {
  const url = `${ENDPOINT}?method=getEventPageData&eventId=${encodeURIComponent(String(eventId))}`
  const res = await fetch(url, { credentials: 'same-origin' })

  if (!res.ok) {
    throw new EventPageApiError(`getEventPageData failed: HTTP ${res.status}`)
  }

  const raw = (await res.json()) as unknown
  const data = camelizeResponse<EventPageData>(raw)

  if (!data.success) {
    throw new EventPageApiError(data.error || 'getEventPageData returned success:false')
  }

  return data
}

// eventPage.cfc goes through the app's shared onCFCRequest dispatch like every other controller
// (form/URL params via <cfargument> binding) - NOT kiosk.cfc's JSON-body special case - so writes
// post application/x-www-form-urlencoded, same as the GET reads' query-string params.
async function callEventPage(method: string, params: Record<string, string | number>): Promise<SignupState> {
  const body = new URLSearchParams({ method, ...Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)])) })
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })

  if (!res.ok) {
    throw new EventPageApiError(`${method} failed: HTTP ${res.status}`)
  }

  const raw = (await res.json()) as unknown
  const data = camelizeResponse<SignupState>(raw)

  if (!data.success) {
    throw new EventPageApiError(data.error || `${method} returned success:false`)
  }

  return data
}

/** Racer-specific signup snapshot (vehicles, per-class entered/blocked/full state, extras) - only
 *  call this when the viewer is a logged-in racer (see App.tsx). Uses the same GET-with-query-
 *  string shape as fetchEventPageData since it's read-only. */
export async function fetchSignupState(eventId: number): Promise<SignupState> {
  const url = `${ENDPOINT}?method=getSignupState&eventId=${encodeURIComponent(String(eventId))}`
  const res = await fetch(url, { credentials: 'same-origin' })

  if (!res.ok) {
    throw new EventPageApiError(`getSignupState failed: HTTP ${res.status}`)
  }

  const raw = (await res.json()) as unknown
  const data = camelizeResponse<SignupState>(raw)

  if (!data.success) {
    throw new EventPageApiError(data.error || 'getSignupState returned success:false')
  }

  return data
}

export function postAddClassEntry(eventId: number, classId: number, vehicleId: number): Promise<SignupState> {
  return callEventPage('addClassEntry', { eventId, classId, vehicleId })
}

export function postAddEventExtra(eventId: number, extraId: number, quantity: number, optionId: number): Promise<SignupState> {
  return callEventPage('addEventExtra', { eventId, extraId, quantity, optionId })
}

export function postUpdateClassEntryVehicle(eventId: number, classId: number, vehicleId: number): Promise<SignupState> {
  return callEventPage('updateClassEntryVehicle', { eventId, classId, vehicleId })
}

export function postRemoveClassEntry(eventId: number, classId: number): Promise<SignupState> {
  return callEventPage('removeClassEntry', { eventId, classId })
}

export function postRemoveEventExtra(eventId: number, extraId: number): Promise<SignupState> {
  return callEventPage('removeEventExtra', { eventId, extraId })
}
